import { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { contentTypeLabel, distributionTypeLabel, type AdminRewardSummaryDto } from '@api/endpoints/adminRewards'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { useAdminRewards } from '../hooks/useAdminRewards'
import { DetailField } from './DetailField'

function ActiveChip({ reward }: { reward: AdminRewardSummaryDto }) {
  return (
    <Chip
      size="small"
      label={reward.isActive ? 'Active' : 'Inactive'}
      color={reward.isActive ? 'success' : 'default'}
      variant={reward.isActive ? 'filled' : 'outlined'}
    />
  )
}

function instances(r: AdminRewardSummaryDto): string {
  return `${r.activeInstanceCount.toLocaleString()} / ${r.totalInstanceCount.toLocaleString()}`
}

const columns: DataTableColumn<AdminRewardSummaryDto>[] = [
  {
    key: 'title',
    header: 'Reward',
    primary: true,
    render: (r) => (
      <Box sx={{ minWidth: 0, maxWidth: 420 }}>
        <Typography variant="body2" fontWeight={600}>
          {r.title}
        </Typography>
        {r.description && (
          // Two lines in the list; the whole description is one click away in the detail dialog.
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              overflowWrap: 'anywhere',
            }}
          >
            {r.description}
          </Typography>
        )}
      </Box>
    ),
  },
  { key: 'group', header: 'Group', render: (r) => r.groupName },
  { key: 'status', header: 'Status', render: (r) => <ActiveChip reward={r} /> },
  { key: 'type', header: 'Type', hideBelow: 'md', render: (r) => contentTypeLabel[r.contentType] ?? r.contentType },
  {
    key: 'distribution',
    header: 'Distribution',
    hideBelow: 'lg',
    render: (r) => distributionTypeLabel[r.distributionType] ?? r.distributionType,
  },
  { key: 'instances', header: 'Active / total', align: 'right', hideBelow: 'md', render: instances },
  { key: 'created', header: 'Created', hideBelow: 'lg', render: (r) => new Date(r.createdAt).toLocaleDateString() },
]

function RewardDetailDialog({ reward, onClose }: { reward: AdminRewardSummaryDto | null; onClose: () => void }) {
  const dialogProps = useMobileDialogProps()
  return (
    <Dialog open={reward != null} onClose={onClose} maxWidth="sm" fullWidth {...dialogProps}>
      {reward && (
        <>
          <DialogTitle sx={{ overflowWrap: 'anywhere' }}>{reward.title}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Box>
                <ActiveChip reward={reward} />
              </Box>
              <DetailField label="Description" value={reward.description || '—'} />
              <DetailField label="Group" value={reward.groupName} />
              <DetailField label="Type" value={contentTypeLabel[reward.contentType] ?? String(reward.contentType)} />
              <DetailField
                label="Distribution"
                value={distributionTypeLabel[reward.distributionType] ?? String(reward.distributionType)}
              />
              <DetailField label="Instances (active / total)" value={instances(reward)} />
              <DetailField label="Total limit" value={reward.totalLimit != null ? reward.totalLimit.toLocaleString() : 'None'} />
              <DetailField
                label="Uses per instance"
                value={reward.usesPerInstance != null ? reward.usesPerInstance.toLocaleString() : 'Unlimited'}
              />
              {reward.fixedExpirationDate && (
                <DetailField label="Expires" value={new Date(reward.fixedExpirationDate).toLocaleString()} />
              )}
              <DetailField label="Created" value={new Date(reward.createdAt).toLocaleString()} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export function RewardsTab() {
  const [query, setQuery] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [selected, setSelected] = useState<AdminRewardSummaryDto | null>(null)

  const { data, isLoading, isError, error, refetch } = useAdminRewards({
    query: query || undefined,
    activeOnly: activeOnly || undefined,
    page: page + 1,
    pageSize,
  })

  return (
    <>
      <DataTable
        rows={data?.items ?? []}
        columns={columns}
        getRowKey={(r) => r.id}
        loading={isLoading}
        error={isError ? (error as Error)?.message || 'Failed to load rewards.' : null}
        onRetry={() => refetch()}
        emptyMessage={query || activeOnly ? 'No rewards match these filters.' : 'No rewards yet.'}
        page={page}
        pageSize={pageSize}
        total={data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => setSelected(row)}
        toolbar={
          <>
            <SearchField
              value={query}
              onSearch={(q) => {
                setQuery(q)
                setPage(0)
              }}
              placeholder="Search rewards"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={activeOnly}
                  onChange={(e) => {
                    setActiveOnly(e.target.checked)
                    setPage(0)
                  }}
                />
              }
              label="Active only"
            />
          </>
        }
        renderCard={(r) => (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {r.title}
              </Typography>
              <ActiveChip reward={r} />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {r.groupName} · {contentTypeLabel[r.contentType] ?? r.contentType} · {instances(r)} active
            </Typography>
          </Box>
        )}
      />
      <RewardDetailDialog reward={selected} onClose={() => setSelected(null)} />
    </>
  )
}
